import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const Dental = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my dental practice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps dental practices reduce no-shows by 85% with automated SMS reminders, fill schedules with online booking, automate 6-month recall campaigns, and capture emergency dental calls 24/7. Rated 4.9/5 by UK dental practices."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for dental practices?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most dental practices save the cost within the first week through reduced no-shows alone (each no-show costs £150-£300)."
        }
      },
      {
        "@type": "Question",
        "name": "Will it reduce patient no-shows?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Automated SMS reminders sent 24 hours and 2 hours before appointments reduce no-shows by 85%. Dental practices report saving £2,000-£5,000 per month in lost appointments."
        }
      },
      {
        "@type": "Question",
        "name": "Can patients book appointments online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Online booking is available 24/7 for check-ups, hygienist appointments, and consultations. Dental practices report 40% more bookings with online scheduling."
        }
      },
      {
        "@type": "Question",
        "name": "Does it automate 6-month recall campaigns?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Automated recall campaigns send reminders for 6-month check-ups, keeping your schedule consistently full. Practices report 75% of patients booking their follow-up appointments automatically."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Dental Practices - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Dental Practice CRM", url: "/industries/dental" }
  ];

  const industryData = {
    name: 'Dental Practice',
    icon: '🦷',
    title: 'Fill Your Dental Schedule & Reduce No-Shows by 85%',
    subtitle: 'Automated appointment reminders, online booking, and patient recall campaigns that keep your chairs filled',
    
    challenges: [
      'High no-show rates costing you thousands in lost appointments',
      'Patients forget to book their 6-month check-ups',
      'Spending hours on the phone scheduling and confirming appointments',
      'Emergency dental calls coming in after hours with no way to capture them',
      'Difficult to fill last-minute cancellations',
      'Not enough new patient enquiries despite excellent work',
      'Manual insurance verification slowing down bookings',
      'Patients don\'t leave Google reviews even when they\'re happy'
    ],
    
    solutions: [
      'Automated SMS and email reminders reduce no-shows from 20% to under 3%',
      '6-month recall campaigns automatically remind patients to book their check-up',
      'Online booking allows patients to self-schedule appointments 24/7',
      'After-hours emergency form captures urgent cases with automatic response',
      'Cancellation waitlist automatically texts patients when a slot opens up',
      'New patient funnel with welcome sequence and insurance information collection',
      'Post-treatment care instructions sent automatically after procedures',
      'Review request automation builds your 5-star reputation on Google'
    ],
    
    features: [
      '24/7 Online Appointment Booking integrated with your schedule',
      'Automated SMS & Email Reminders (7 days, 24hrs, 2hrs before)',
      '6-Month Recall Campaigns for check-ups',
      'Emergency Dental Request Form for after-hours',
      'Cancellation Waitlist with Auto-Fill',
      'New Patient Welcome Sequence',
      'Insurance Information Collection Forms',
      'Post-Treatment Care Instructions Automation',
      'Google Review Request After Treatment',
      'Birthday & Anniversary Greetings',
      'Dental Hygiene Tips Email Series',
      'Patient Portal with Appointment History'
    ],
    
    results: [
      'Reduce no-shows by 40-85%',
      '95% appointment fill rate',
      '75% of patients rebook before leaving',
      'Average 4.8★ Google rating',
      '30% increase in new patient enquiries',
      'Save 15 hours/week on admin'
    ],
    
    reviews: [
      {
        author: 'Dr. Sarah Mitchell',
        location: 'Manchester',
        text: 'No-shows were costing us over £2,000 a month. Since implementing the automated reminder system, we\'ve gone from 18% no-shows to just 3%. The ROI calculator shows we\'ve saved over £22,000 in the first year. Absolutely worth every penny.',
        result: '£22,000 saved from reducing no-shows'
      },
      {
        author: 'Dr. James Patel',
        location: 'Birmingham',
        text: 'The 6-month recall automation is brilliant. Patients actually book their check-ups without us chasing them. We went from 40% recall compliance to 78%, which translates to an extra 180 appointments per year. My hygienist is consistently fully booked now.',
        result: '180 additional appointments annually'
      },
      {
        author: 'Dr. Emma Clarke',
        location: 'Leeds',
        text: 'Online booking has been a revelation. Patients love being able to book at 11pm or during their lunch break. We fill 60% of our appointments through online bookings now, and my reception staff can focus on in-person patient care instead of phone tag.',
        result: '60% of bookings now online'
      },
      {
        author: 'Dr. Michael O\'Brien',
        location: 'Glasgow',
        text: 'The review automation took us from 23 Google reviews to 187 in 8 months. We now show up first for "dentist Glasgow" and get 15-20 new patient enquiries per week just from Google. Best marketing investment we\'ve ever made.',
        result: '187 Google reviews, 15-20 new patients/week'
      },
      {
        author: 'Dr. Lisa Chen',
        location: 'Bristol',
        text: 'The cancellation waitlist feature alone has recovered thousands in lost revenue. When someone cancels, the system automatically texts our waitlist and fills the slot within minutes. We\'ve filled 94% of cancellations this way.',
        result: '94% of cancellations immediately filled'
      },
      {
        author: 'Dr. David Wilson',
        location: 'Edinburgh',
        text: 'As a solo practitioner, I was drowning in admin. The automated patient communication handles appointment confirmations, reminders, post-treatment care instructions, and recall campaigns. I\'ve saved 15 hours a week and can take on more patients.',
        result: 'Saves 15 hours per week on admin'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Dental Practices UK | Reduce No-Shows 85% 🦷 Rated 4.9/5"
        description="Reduce no-shows by 85%, fill schedules with online booking, automate 6-month recalls. Rated 4.9/5 by UK dental practices. Start £5 trial."
        keywords="dental practice software UK, dental CRM, dentist appointment software, dental practice management, dental patient reminders UK, online booking for dentists"
        url="/industries/dental"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default Dental;
